import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Email,
  Phone,
  LocationOn,
  WhatsApp
} from '@mui/icons-material';
import { useSettings } from '../context/SettingsContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { settings, loading } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const handleFAQClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Si on est déjà sur la page d'accueil, scroll vers la section
      const faqSection = document.getElementById('faq');
      if (faqSection) {
        const rect = faqSection.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = rect.top + scrollTop - 80; // 80px pour la navbar
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
        // Mettre à jour l'URL avec le hash
        window.history.pushState(null, '', '/#faq');
      }
    } else {
      // Sinon, naviguer vers la page d'accueil avec le hash
      navigate('/#faq');
      // Le useEffect dans Home.js gérera le scroll automatiquement
      // Mais on ajoute aussi un fallback au cas où
      setTimeout(() => {
        const faqSection = document.getElementById('faq');
        if (faqSection) {
          const rect = faqSection.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetPosition = rect.top + scrollTop - 80;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }, 1000);
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#0f1629',
        color: 'white',
        mt: 'auto',
        py: 6
      }}
    >
      <Container maxWidth="lg">
        <Grid 
          container 
          spacing={3} 
          sx={{ 
           // alignItems: 'stretch',  Important pour étirer toutes les colonnes
            // flexWrap: { xs: 'wrap', md: 'nowrap' }
          }}
        >
          {/* Logo et description - 50% */}
          <Grid 
            // item 
            // xs={12} 
            // md={6} 
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100%',
              alignItems: 'stretch',
              justifyContent: 'stretch',
              flexDirection: 'column',
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: 'auto',
              height: 'auto',
              width: 'auto',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          >
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 3, flex: 1 }}>
                {!loading && settings.site_logo ? (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <img 
                      src={settings.site_logo} 
                      alt="Logo" 
                      style={{ 
                        height: '70px', 
                        width: '80px', 
                        borderRadius: '20%',
                        marginRight: '18px',
                        objectFit: 'cover'
                      }} 
                    />
                    <Typography variant="h5" component="div" sx={{ 
                      fontWeight: 'bold', 
                      background: 'white', 
                      backgroundClip: 'text', 
                      WebkitBackgroundClip: 'text', 
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {settings.site_name || 'Tchamba Martketplace'}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="h5" component="div" sx={{ 
                    fontWeight: 'bold', 
                    background: 'linear-gradient(45deg, #1976d2, #42a5f5)', 
                    backgroundClip: 'text', 
                    WebkitBackgroundClip: 'text', 
                    WebkitTextFillColor: 'transparent',
                    mb: 2
                  }}>
                    {!loading && settings.site_name ? settings.site_name : 'Tchamba Martketplace'}
                  </Typography>
                )}
                <Box 
                  sx={{ 
                    minHeight: '60px', // Hauteur minimale
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}
                >
                  <Typography 
                    variant="body2" 
                    color="grey.300" 
                    sx={{ 
                      lineHeight: 1.6,
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                      maxWidth: '100%',
                      flex: 1
                    }}
                  >
                    {!loading && settings.site_description ? settings.site_description : 'Votre plateforme e-commerce de confiance. Découvrez une sélection exceptionnelle de produits et boutiques locales de qualité.'}
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1}>
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: 'grey.400', 
                    '&:hover': { color: '#1877f2' } 
                  }}
                >
                  <Facebook />
                </IconButton>
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: 'grey.400', 
                    '&:hover': { color: '#1da1f2' } 
                  }}
                >
                  <Twitter />
                </IconButton>
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: 'grey.400', 
                    '&:hover': { color: '#e4405f' } 
                  }}
                >
                  <Instagram />
                </IconButton>
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: 'grey.400', 
                    '&:hover': { color: '#0077b5' } 
                  }}
                >
                  <LinkedIn />
                </IconButton>
              </Stack>
            </Box>
          </Grid>
  
          {/* Liens rapides - 25% */}
          <Grid 
            // item 
            // xs={12} 
            // md={3}  
            sx={{ 
              display: 'flex',
              flexDirection: 'column'
            }}
          > 
            <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
              Navigation
            </Typography>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Link href="/" color="grey.300" underline="hover" sx={{ '&:hover': { color: 'white' } }}>
                Accueil
              </Link>
              <Link href="/products" color="grey.300" underline="hover" sx={{ '&:hover': { color: 'white' } }}>
                Produits
              </Link>
              <Link href="/boutiques" color="grey.300" underline="hover" sx={{ '&:hover': { color: 'white' } }}>
                Boutiques
              </Link>
              <Link href="/services" color="grey.300" underline="hover" sx={{ '&:hover': { color: 'white' } }}>
                Services
              </Link>
              <Link href="/contact" color="grey.300" underline="hover" sx={{ '&:hover': { color: 'white' } }}>
                Contact
              </Link>
            </Stack>
          </Grid>
  
          {/* Support - 25% */}
          <Grid 
            // item 
            // xs={12} 
            // md={3} 
            sx={{ 
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
              Support
            </Typography>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Link href="/terms-of-service" color="grey.300" underline="hover" sx={{ '&:hover': { color: 'white' } }}>
                Conditions d'utilisation
              </Link>
              <Link 
                href="/#faq" 
                onClick={handleFAQClick}
                color="grey.300" 
                underline="hover" 
                sx={{ '&:hover': { color: 'white' }, cursor: 'pointer' }}
              >
                FAQ
              </Link>
              <Link href="/privacy-policy" color="grey.300" underline="hover" sx={{ '&:hover': { color: 'white' } }}>
                Politique de confidentialité
              </Link>
            </Stack>
          </Grid>
        </Grid>
  
        <Divider sx={{ my: 4, borderColor: 'grey.700' }} />
  
        {/* Footer bottom */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2
        }}>
          <Typography variant="body2" color="grey.400">
            © {currentYear} Tchamba Martketplace. Tous droits réservés.
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Link href="/terms-of-service" color="grey.400" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Conditions d'utilisation
            </Link>
            <Typography color="grey.600">•</Typography>
            <Link href="/privacy-policy" color="grey.400" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Politique de confidentialité
            </Link>
            <Typography color="grey.600">•</Typography>
            <Link href="/cookies" color="grey.400" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Cookies
            </Link>
          </Box>
        </Box>
  
        {/* Badges de confiance */}
        <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          <Chip 
            label="Paiement sécurisé (bientôt)" 
            size="small" 
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.1)', 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
            }} 
          />
          {/* <Chip 
            label="Livraison rapide (bientôt)" 
            size="small" 
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.1)', 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
            }} 
          /> */}
          <Chip 
            label="Support 24/7" 
            size="small" 
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.1)', 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
            }} 
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
